import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CampaignNameSection } from 'src/components-bl/EmailMarketing/CampaignModalPage/components/CampaignSideBar/components/CampaignNameSection';
import { RecommendationTypeSection } from './components/RecommendationTypeSection';
import { TrackingParametersSection } from './components/TrackingParametersSection/TrackingParametersSection';
import { EmailVariablesSection } from './components/EmailVariablesSection/EmailVariablesSection';
import { AccordionSideBar } from 'src/components-dummy/AccordionSideBar';
import { EmailDisplaySettingsSection } from './components/EmailDisplaySettingsSection/EmailDisplaySettingsSection';
import { LayoutSection } from './components/LayoutSection';
import { useValidateSchema } from 'src/hooks';
import { useAccordionExpansion } from 'src/components-dummy/AccordionSideBar/hooks/useAccordionExpansion';
import { getErrorMessage } from '../../../get-error-message';
import { campaignValidationSchema } from '../../campaign-validation-schema';
import { CampaignDraft } from '../../../types/campaign-draft';
import { Dispatch } from 'src/components-bl';
import { AccordionSection } from '../../../types';
import { ExpandableSection } from '../../../constants/expandable-section';
import { StyledTab, TabWrapper } from './CampaignSideBar.styles';

interface CampaignSideBarProps {
  shopId: number;
  dispatch: Dispatch;
  navigateToCampaigns: () => void;
  onFormValidationChange: (isValid: boolean) => void;
  campaignDraft: CampaignDraft;
  setCampaignDraft: React.Dispatch<React.SetStateAction<CampaignDraft>>;
  isLoading: boolean;
  isMobile: boolean;
}

export const CampaignSideBar = ({
  shopId,
  dispatch,
  navigateToCampaigns,
  onFormValidationChange,
  campaignDraft,
  setCampaignDraft,
  isLoading,
  isMobile,
}: CampaignSideBarProps): JSX.Element => {
  const { expandedSection, handleSectionToggle } = useAccordionExpansion(null);
  const isEditEnabled = !isLoading;
  const [tabs, setTab] = useState<'Setup' | 'Design'>('Setup');

  const { errors, validate, isValid } = useValidateSchema<CampaignDraft>({
    schema: campaignValidationSchema,
    validateOnStart: false,
    initialData: {
      campaignName: campaignDraft.campaignName,
      strategy: campaignDraft.strategy,
      widgetMaker: campaignDraft.widgetMaker,
    },
  });

  useEffect(() => {
    const validationData = {
      dataToValidate: {
        campaignName: campaignDraft.campaignName || '',
        strategy: campaignDraft.strategy,
        collectionId: campaignDraft.collectionId || undefined,
        widgetMaker: campaignDraft.widgetMaker || undefined,
      },
    };

    validate(validationData);
    onFormValidationChange(isValid);
  }, [campaignDraft, validate, onFormValidationChange, isValid]);

  const onChange = useCallback(
    (partialCampaign: Partial<CampaignDraft>) => {
      setCampaignDraft(prevCampaign => ({ ...prevCampaign, ...partialCampaign }));
    },
    [setCampaignDraft]
  );

  const setupSections: AccordionSection[] = [
    {
      id: ExpandableSection.CampaignName,
      title: 'Campaign Name',
      content: (
        <CampaignNameSection
          value={campaignDraft.campaignName || ''}
          onChange={onChange}
          errors={errors}
        />
      ),
      isExpanded: expandedSection === ExpandableSection.CampaignName,
    },
    {
      id: ExpandableSection.SelectRecommendationType,
      title: 'Recommendation Type ',
      content: (
        <RecommendationTypeSection
          shopId={shopId}
          dispatch={dispatch}
          campaignDraft={{
            strategy: campaignDraft.strategy,
            collectionId: campaignDraft.collectionId,
          }}
          onChange={onChange}
          errors={errors}
        />
      ),
      isExpanded: expandedSection === ExpandableSection.SelectRecommendationType,
    },
    {
      id: ExpandableSection.EmailVariables,
      title: 'Email Variables',
      content: (
        <EmailVariablesSection
          widgetMaker={campaignDraft.widgetMaker}
          onChange={onChange}
          shopId={shopId}
        />
      ),
      isExpanded: expandedSection === ExpandableSection.EmailVariables,
    },
    {
      id: ExpandableSection.TrackingParameters,
      title: 'Tracking Parameters',
      content: (
        <TrackingParametersSection
          widgetMaker={campaignDraft.widgetMaker}
          onChange={onChange}
          getErrorMessage={fieldKey => getErrorMessage(errors, 'UTMParameters', fieldKey)}
        />
      ),
      isExpanded: expandedSection === ExpandableSection.TrackingParameters,
    },
  ];

  const designSections: AccordionSection[] = [
    {
      id: ExpandableSection.Layout,
      title: 'Choose Layout',
      content: (
        <LayoutSection
          onChange={onChange}
          widgetMaker={campaignDraft.widgetMaker}
          getErrorMessage={fieldKey => getErrorMessage(errors, 'width', fieldKey)}
          isMobile={isMobile}
        />
      ),
      isExpanded: expandedSection === ExpandableSection.Layout,
    },
    {
      id: ExpandableSection.EmailDisplaySettings,
      title: 'Email Setup',
      content: (
        <EmailDisplaySettingsSection
          widgetMaker={campaignDraft.widgetMaker}
          onChange={onChange}
          errors={errors}
        />
      ),
      isExpanded: expandedSection === ExpandableSection.EmailDisplaySettings,
    },
  ];

  const filteredSections = tabs === 'Setup' ? setupSections : designSections;

  const tabWrapperRef = useRef(null);
  const tabWrapperHeight = tabWrapperRef?.current?.offsetHeight;

  return (
    <AccordionSideBar
      header='Create Campaign'
      isLoading={isLoading}
      navigateToList={navigateToCampaigns}
      setExpendedSection={handleSectionToggle}
      disabled={!isEditEnabled}
      sections={filteredSections}
      tabWrapperHeight={tabWrapperHeight}
    >
      <TabWrapper ref={tabWrapperRef}>
        <StyledTab label='Setup' onClick={() => setTab('Setup')} isActive={tabs === 'Setup'} />
        <StyledTab label='Design' onClick={() => setTab('Design')} isActive={tabs === 'Design'} />
      </TabWrapper>
    </AccordionSideBar>
  );
};
